import { useNotification, IRemoteNotification } from '@magicbell/react-headless';
import { Avatar, Button, Stack, Typography, useTheme } from '@mui/material';
import { Circle } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import Markdown from 'src/components/markdown';
import { useDownloadExport } from 'src/hooks/query/exportHooks';
import { processNotification } from './utils';

const SPACING = 2.5;

type Props = {
  rawNotification: IRemoteNotification;
};

function HeadlessNotification({ rawNotification }: Props) {
  const notification = useNotification(rawNotification);
  const theme = useTheme();

  const handleClick = () => {
    if (notification.isRead) notification.markAsUnread();
    else notification.markAsRead();
  };

  const { download, isDownloading } = useDownloadExport();

  const { title, content, icon, color, palette, actionText, actionUrl, exportId } =
    processNotification(theme, notification);
  return (
    <Button
      key={notification.id}
      fullWidth
      variant="text"
      color="inherit"
      onClick={() => handleClick()}
      sx={{
        borderRadius: 0,
        backgroundColor: notification.readAt ? 'transparent' : 'transparent',
      }}
    >
      <Stack
        p={SPACING}
        direction="row"
        alignItems="start"
        justifyContent="start"
        flexWrap="nowrap"
        position="relative"
        width="100%"
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            marginRight: 2,
            color: palette.main,
            backgroundColor: theme.palette.grey[100],
            flexShrink: 0,
          }}
          color="primary"
        >
          {icon}
        </Avatar>
        <Stack position="absolute" top={26} right={20}>
          {!notification.readAt && <Circle color="primary" sx={{ width: 8, height: 8 }} />}
        </Stack>
        <Stack
          direction="column"
          alignItems="start"
          justifyContent="start"
          gap={0.5}
          paddingRight={1.5}
          textAlign="left"
          flexGrow={1}
        >
          <Typography variant="subtitle2" fontWeight={notification.readAt ? 400 : 600}>
            {title}
          </Typography>
          <Markdown
            sx={{
              ...theme.typography.body2,
              '& p': { margin: 0 },
              '& ul': { fontSize: 'inherit', lineHeight: '1.5' },
              '& ul > li': { lineHeight: '1.5' },
            }}
          >
            {content || ''}
          </Markdown>
          <Typography variant="caption" color="text.secondary">
            {formatDistanceToNow(new Date((rawNotification.sentAt || 0) * 1000), {
              addSuffix: true,
            })}
          </Typography>
          {(actionUrl || exportId) && (
            <Button
              variant="contained"
              size="small"
              component={actionUrl ? 'a' : 'button'}
              target={actionUrl ? '_blank' : undefined}
              rel={actionUrl ? 'noopener noreferrer' : undefined}
              color={color}
              href={actionUrl}
              loading={isDownloading}
              onClick={exportId ? () => download(exportId) : undefined}
              sx={{ marginTop: 1 }}
            >
              {actionText || 'View Details'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Button>
  );
}

export default HeadlessNotification;
